import { renderWithPagination, useForm } from '@metorial/data-hooks';
import { PageHeader } from '@metorial/layout';
import { useOrganizationInvites } from '@metorial/state';
import {
  Button,
  confirm,
  Dialog,
  Input,
  RenderDate,
  Select,
  showModal,
  Spacer,
  Text,
  toast
} from '@metorial/ui';
import { Table } from '@metorial/ui-product';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { INVITE_QUERY_PARAM } from '../../../../shell';
import { Chip, EmptyText, Stack, useSettingsScope } from '../_kit';

// Settings › Team › Invites (cloud-spec §7.1). BlackOS doesn't send email:
// an email invite is honoured when that person next signs in with Google
// Workspace, and is the only way into the dashboard besides
// BLACKOS_ADMIN_EMAILS. There is no invite link: the core API turns away
// anyone who isn't a member, so nobody could accept one.

type Role = 'member' | 'admin';

let statusTone = {
  pending: 'orange',
  accepted: 'green',
  rejected: 'red',
  expired: 'gray',
  deleted: 'gray'
} as const;

let showInviteModal = (invites: ReturnType<typeof useOrganizationInvites>) =>
  showModal(({ dialogProps, close }) => {
    let create = invites.createByEmailMutator();

    let form = useForm({
      initialValues: { email: '', role: 'member' as Role, message: '' },
      onSubmit: async values => {
        let [res] = await create.mutate({
          email: values.email.trim(),
          role: values.role,
          message: values.message || undefined
        });
        if (!res) return;

        close();
        toast.success(
          `Invite created. ${values.email.trim()} joins as ${values.role == 'admin' ? 'an admin' : 'a member'} the next time they sign in.`
        );
      },
      schema: yup =>
        yup.object({
          email: yup.string().email('Enter a valid email').required('Email is required'),
          role: yup.mixed<Role>().oneOf(['member', 'admin']).required(),
          message: yup.string().optional()
        })
    });

    return (
      <Dialog.Wrapper {...dialogProps}>
        <Dialog.Title>Invite User</Dialog.Title>
        <Dialog.Description>Invite a new user to your organization.</Dialog.Description>

        <form onSubmit={form.handleSubmit}>
          <Stack $gap={12}>
            <div>
              <Input label="Email" type="email" autoFocus {...form.getFieldProps('email')} />
              <form.RenderError field="email" />
            </div>

            <Select
              label="Role"
              items={[
                { id: 'member', label: 'Member' },
                { id: 'admin', label: 'Admin' }
              ]}
              value={form.values.role}
              onChange={v => form.setFieldValue('role', v)}
            />

            <Input
              label="Message"
              as="textarea"
              minRows={3}
              placeholder="Optional"
              {...form.getFieldProps('message')}
            />

            <Text size="1" color="gray600">
              Email delivery isn't set up, so no email is sent. They join with this role the
              next time they sign in with Google.
            </Text>
          </Stack>

          <Spacer size={15} />

          <Dialog.Actions>
            <Button size="2" variant="soft" type="button" onClick={close}>
              Cancel
            </Button>
            <Button size="2" type="submit" loading={create.isLoading}>
              Send Invite
            </Button>
          </Dialog.Actions>
        </form>
      </Dialog.Wrapper>
    );
  });

export let OrganizationSettingsInvitesPage = () => {
  let scope = useSettingsScope();
  let invites = useOrganizationInvites(scope.organizationId);
  let remove = invites.deleteMutator();
  let [search, setSearch] = useSearchParams();

  // The shell's `+ › Invite` and the Members page open the dialog via ?invite=1.
  useEffect(() => {
    if (!search.get(INVITE_QUERY_PARAM) || !scope.isAdmin) return;
    let next = new URLSearchParams(search);
    next.delete(INVITE_QUERY_PARAM);
    setSearch(next, { replace: true });
    showInviteModal(invites);
  }, [search, scope.isAdmin]);

  return (
    <>
      <PageHeader
        title="Invites"
        description="Invite people to join your organization."
        actions={
          scope.isAdmin && (
            <Button size="2" onClick={() => showInviteModal(invites)}>
              Invite
            </Button>
          )
        }
      />

      <Stack>
        {renderWithPagination(invites)(invites =>
          invites.data.items.length == 0 ? (
            <EmptyText>
              No invites yet. Invite users to your organization by clicking the "Invite" button
              above.
            </EmptyText>
          ) : (
            <Table
              headers={['Email', 'Role', 'Status', 'Created', 'Expires', '']}
              data={invites.data.items.map(invite => [
                <Text size="2" weight="strong" style={{ padding: '8px 0' }}>
                  {invite.email ?? 'Invite link'}
                </Text>,
                <Chip tone={invite.role == 'admin' ? 'purple' : 'gray'}>
                  {invite.role == 'admin' ? 'Admin' : 'Member'}
                </Chip>,
                <Chip tone={statusTone[invite.status as keyof typeof statusTone] ?? 'gray'}>
                  {invite.status.charAt(0).toUpperCase() + invite.status.slice(1)}
                </Chip>,
                <RenderDate date={invite.createdAt} />,
                invite.expiresAt ? <RenderDate date={invite.expiresAt} /> : '-',
                scope.isAdmin && invite.status == 'pending' ? (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      size="1"
                      variant="outline"
                      color="red"
                      onClick={() =>
                        confirm({
                          title: 'Revoke invite?',
                          description: `${invite.email ?? 'This invite'} will no longer be able to join with it.`,
                          confirmText: 'Revoke',
                          onConfirm: async () => {
                            let [res] = await remove.mutate({ organizationInviteId: invite.id });
                            if (res) toast.success('Invite revoked.');
                          }
                        })
                      }
                    >
                      Revoke
                    </Button>
                  </div>
                ) : (
                  ''
                )
              ])}
            />
          )
        )}
      </Stack>
    </>
  );
};
